import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

interface ShadcnDatePickerProps {
  value?: Date;
  onChange: (date: Date) => void;
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  classNames?: {
    day?: string;
    cell?: string;
    head_cell?: string;
  };
  minDate?: Date;
  popoverProps?: React.HTMLAttributes<HTMLDivElement>;
  placeholder?: string;
  // Optional predicate to disable specific dates
  isDateDisabled?: (date: Date) => boolean;
}

export const ShadcnDatePicker: React.FC<ShadcnDatePickerProps> = ({
  value,
  onChange,
  open,
  onOpenChange,
  classNames = {},
  popoverProps = {},
  placeholder = "Select date",
  minDate,
  isDateDisabled,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(value ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
  const [isOpen, setIsOpen] = useState<boolean>(open ?? false);

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayStart = startOfDay(new Date());
  const min = startOfDay(minDate ?? todayStart);

  const isAtMinMonth =
    currentDate.getFullYear() === min.getFullYear() &&
    currentDate.getMonth() === min.getMonth();

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setCurrentDate(value);
    }
  }, [value]);

  useEffect(() => {
    setIsOpen(open ?? false);
  }, [open]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonth = new Date(year, month, 0);
      const prevDay = prevMonth.getDate() - startingDayOfWeek + i + 1;
      days.push({
        day: prevDay,
        isCurrentMonth: false,
        isToday: false,
        date: new Date(year, month - 1, prevDay),
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const today = new Date();
      days.push({
        day,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString(),
        date,
      });
    }

    const remainingCells = 42 - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        isToday: false,
        date: new Date(year, month + 1, day),
      });
    }

    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange(date);
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const isSelected = (date: Date) =>
    selectedDate && date.toDateString() === selectedDate.toDateString();

  const formatDisplayDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

  const days = getDaysInMonth(currentDate);

  return (
    <div className="w-auto flex justify-start items-center  ">
      <div className="">
        <div
          className="hidden items-center justify-between w-full p-3 border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 transition-colors"
          onClick={() => {
            const newOpen = !isOpen;
            setIsOpen(newOpen);
            onOpenChange?.(newOpen);
          }}
        >
          <span className="text-gray-700 text-[1.15rem] sm:text-[1.25rem]">
            {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
          </span>
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
        </div>

        {isOpen && (
          <div className="  mt-1 bg-transparent z-50 py-3 sm:p-4 ">
            <div className="flex items-center justify-between mb-8 px-2">
              <button
                onClick={() => !isAtMinMonth && navigateMonth(-1)}
                disabled={isAtMinMonth}
                className={`p-1 sm:p-2 rounded-md transition-colors ${isAtMinMonth
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-gray-100"
                  }`}
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              <h2 className="text-[1.3rem]  font-semibold text-gray-900">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>

              <button
                onClick={() => navigateMonth(1)}
                className="p-1 sm:p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="grid grid-cols-7  gap-[2px]  ">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className={
                    classNames.head_cell ||
                    "text-[1.25rem] sm:text-[1.15rem] font-medium text-gray-500 py-2"
                  }
                >
                  {day}
                </div>
              ))}

              {days.map((dayObj, index) => {
                const isPastDay = startOfDay(dayObj.date) < min;
                const isDisabledByProp = !!isDateDisabled?.(startOfDay(dayObj.date));
                const isDisabled = !dayObj.isCurrentMonth || isPastDay || isDisabledByProp;
                return (
                  <button
                    key={index}
                    name={dayObj.date.toISOString().split("T")[0]}
                    onClick={() => !isDisabled && handleDateSelect(dayObj.date)}
                    className={`
                    w-full h-full px-1 py-4 flex items-center justify-center text-[1.4rem] sm:text-[1.3rem] rounded-md transition-all duration-200
                    ${isDisabled ? "text-gray-300 cursor-not-allowed" : "text-gray-900 hover:bg-gray-100 cursor-pointer"}
                    ${isSelected(dayObj.date)
                        ? "bg-blue-500 text-[#ffffff] hover:text-[#ffffff] hover:bg-blue-600"
                        : ""
                      }
                    ${dayObj.isToday && !isSelected(dayObj.date)
                        ? "bg-blue-50 font-bold border-2 border-blue-500 text-blue-700"
                        : ""
                      }
                    ${classNames.day || ""}
                  `}
                    disabled={isDisabled}
                  >
                    {dayObj.day}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
